export type FilterCategory = 'Disease' | 'Gene' | 'Variant' | 'Chemical' | 'Source' | 'Association' | 'Other';

const CATEGORY_ORDER: FilterCategory[] = [
  'Disease',
  'Gene',
  'Variant',
  'Chemical',
  'Source',
  'Association',
  'Other',
];

export function getFilterCategory(filter: { name?: string; label?: string }): FilterCategory {
  const key = `${filter.name ?? ''} ${filter.label ?? ''}`.toLowerCase();

  if (/(^|[\s_])source|associationtype|assoc_type/.test(key)) return 'Source';
  if (/disease|msh|umls|hpo|\bdo\b|prevalence|inheritance|cui/.test(key)) return 'Disease';
  if (/gene|symbol|ensembl|ncbi|dsi|dpi|pli|protein|pathway/.test(key)) return 'Gene';
  if (/variant|dbsnp|clinical.?signif|allele|chromosome|rsid/.test(key)) return 'Variant';
  if (/chem|chemical|molecule/.test(key)) return 'Chemical';
  if (/score|\bei\b|\bel\b|year|pmid|nct|evidence/.test(key)) return 'Association';

  return 'Other';
}

export type ActiveFilterChip = {
  key: string;
  label: string;
  category: FilterCategory;
  filterName: string;
  itemValue?: string;
  filter: any;
};

export function buildActiveFilterChips(
  activeFiltersWithLabels: any[],
  getFilterLabel: (filter: any) => string,
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  activeFiltersWithLabels.forEach((filter, index) => {
    const category = getFilterCategory(filter);

    if (Array.isArray(filter.value) && filter.value.length > 0) {
      filter.value.forEach((itemValue: unknown, i: number) => {
        const valueStr = String(itemValue);
        chips.push({
          key: `${filter.name}-${valueStr}-${i}`,
          label: `${filter.label}: ${valueStr}`,
          category,
          filterName: filter.name,
          itemValue: valueStr,
          filter,
        });
      });
      return;
    }

    chips.push({
      key: `${filter.name}-${filter.operator || ''}-${index}`,
      label: getFilterLabel(filter),
      category,
      filterName: filter.name,
      filter,
    });
  });

  return chips;
}

export function groupChipsByCategory(chips: ActiveFilterChip[]): Array<{
  category: FilterCategory;
  chips: ActiveFilterChip[];
}> {
  const byCategory = new Map<FilterCategory, ActiveFilterChip[]>();

  chips.forEach(chip => {
    const list = byCategory.get(chip.category) ?? [];
    list.push(chip);
    byCategory.set(chip.category, list);
  });

  return CATEGORY_ORDER.filter(category => byCategory.has(category)).map(category => ({
    category,
    chips: byCategory.get(category)!,
  }));
}

export function getActiveFiltersSummary(chips: ActiveFilterChip[]): {
  chipCount: number;
  categoryCount: number;
} {
  const categories = new Set(chips.map(chip => chip.category));
  return {
    chipCount: chips.length,
    categoryCount: categories.size,
  };
}
